import { http } from '@/lib/axios';
import { env } from '@/config/env';
import type { AxiosProgressEvent } from 'axios';

export type UploadProgressHandler = (percent: number) => void;

/**
 * Generic multipart upload used by every feature-specific upload call
 * (poster/backdrop/logo/trailer/video/subtitle/avatar/banner image).
 * The field name and response shape differ per endpoint, so callers
 * pass those in rather than this trying to be one-size-fits-all.
 */
export async function uploadFile<TResponse>(
  url: string,
  fieldName: string,
  file: File,
  extraFields?: Record<string, string | number | boolean>,
  onProgress?: UploadProgressHandler,
): Promise<TResponse> {
  const formData = new FormData();
  formData.append(fieldName, file);

  if (extraFields) {
    for (const [key, value] of Object.entries(extraFields)) {
      formData.append(key, String(value));
    }
  }

  const { data } = await http.post<{ data: TResponse }>(url, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
    onUploadProgress: (event: AxiosProgressEvent) => {
      if (onProgress && event.total) {
        onProgress(Math.round((event.loaded / event.total) * 100));
      }
    },
  });

  return data.data;
}

/** Origin the API is actually served from, derived from VITE_API_BASE_URL so it works whether that's an absolute cross-origin URL (prod) or a relative path proxied to the same origin (dev). */
function getMediaOrigin(): string {
  try {
    return new URL(env.apiBaseUrl, window.location.origin).origin;
  } catch {
    return window.location.origin;
  }
}

/** Backend returns media URLs as root-relative paths (/api/v1/media/...); resolve them against the API's own origin rather than the page's, since admin-panel and the API can be on different domains in production. */
export function resolveMediaUrl(path: string | null | undefined): string | undefined {
  if (!path) return undefined;
  if (/^https?:\/\//i.test(path)) return path;

  return `${getMediaOrigin()}${path}`;
}
